import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import socket from './lib/socket';
import { api } from './lib/api';
import { setAgents as setAgentRegistry } from './lib/agentRegistry';
import { AppShell, AppBar, NavigationRail, NavigationBar, IconButton, Icon, Text, Stack, Cluster, Box, Container, List, Button, Menu, Page } from '@iqueue/iq-ui';

import ChatView from './components/chat/ChatView';
import CreateRoomDialog from './components/rooms/CreateRoomDialog';
import RoomMembersDialog from './components/rooms/RoomMembersDialog';
import RoomMemoryDialog from './components/rooms/RoomMemoryDialog';
import RoomFilesDialog from './components/rooms/RoomFilesDialog';
import { useConfirm } from './components/ui/ConfirmDialog';
import TasksPanel from './components/panels/TasksPanel';
import AdminPanel from './components/admin/AdminPanel';
import { clearAdminCache } from './components/admin/adminData';

const HISTORY_PAGE = 100;

const NAV_TITLES = { rooms: 'Комнаты', tasks: 'Задачи', admin: 'Админка' };

const TAB_PATHS = ['rooms', 'tasks', 'admin'];

const plural = (n, one, few, many) => {
  const t = n % 100;
  if (t > 10 && t < 20) return many;
  const d = n % 10;
  return d === 1 ? one : (d >= 2 && d <= 4 ? few : many);
};

/** Что видно про комнату в строке: её описание, кто внутри и в каком контексте. */
function roomSummary(room, contexts) {
  const members = room.members || [];
  const people = members.filter(m => m.participant_type === 'user').length;
  const bots = members.filter(m => m.participant_type === 'agent').map(m => `@${m.participant}`);
  const ctx = (contexts || []).find(c => c.id === room.default_context_id);
  const parts = [
    room.description || null,
    `${people} ${plural(people, 'человек', 'человека', 'человек')}`,
    bots.length ? bots.join(', ') : 'без агентов',
    ctx ? ctx.name : null,
  ];
  return parts.filter(Boolean).join(' · ');
}

export default function Workspace({ me, myRole, onLoggedOut }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Tab + room derived from the URL: /chat/:roomId · /rooms · /tasks · /admin
  const seg = location.pathname.split('/').filter(Boolean);
  const navValue = TAB_PATHS.includes(seg[0]) ? seg[0] : 'chat';
  const urlRoom = seg[0] === 'chat' && seg[1] ? decodeURIComponent(seg[1]) : null;

  const [agents, setAgentsState] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(urlRoom || localStorage.getItem('baseagent_room') || 'main');
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [runs, setRuns] = useState(new Map());
  // message_id → runId: the row key a finished run hands to the message it
  // produced, so the working indicator morphs instead of being swapped out.
  const [runKeys, setRunKeys] = useState(new Map());
  const [tasks, setTasks] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  // Диалог участников открывается для ЛЮБОЙ комнаты, не только для открытой:
  // держим здесь саму комнату, а не флаг.
  const [membersRoom, setMembersRoom] = useState(null);
  const [memoryRoom, setMemoryRoom] = useState(null);
  const [filesRoom, setFilesRoom] = useState(null);
  const [contexts, setContexts] = useState([]);
  const [confirmDialog, confirm] = useConfirm();
  const [toast, setToast] = useState(null); // { text, icon }

  const currentRoomRef = useRef(currentRoom);
  const runsRef = useRef(runs);
  const meRef = useRef(me);
  const seenEventsRef = useRef(new Set());
  const toastTimer = useRef(null);

  useEffect(() => { currentRoomRef.current = currentRoom; }, [currentRoom]);
  useEffect(() => { runsRef.current = runs; }, [runs]);

  // Navigate tabs / rooms through the URL so every view is linkable.
  const goTab = useCallback((value) => {
    if (value === 'chat') navigate(`/chat/${encodeURIComponent(currentRoomRef.current)}`);
    else navigate(`/${value}`);
  }, [navigate]);

  // URL is the source of truth for the open room; keep state + storage in sync.
  useEffect(() => {
    if (urlRoom && urlRoom !== currentRoomRef.current) {
      setCurrentRoom(urlRoom);
      localStorage.setItem('baseagent_room', urlRoom);
    }
  }, [urlRoom]);

  // Bare "/" or "/chat" → canonical /chat/:room
  useEffect(() => {
    if (navValue === 'chat' && !urlRoom) {
      navigate(`/chat/${encodeURIComponent(currentRoomRef.current)}`, { replace: true });
    }
  }, [navValue, urlRoom, navigate]);
  useEffect(() => { meRef.current = me; }, [me]);

  const showToast = useCallback((text, icon) => {
    setToast({ text, icon });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  // ===== base data =====
  const loadAgents = useCallback(async () => {
    try {
      const { agents: list } = await api.agents();
      setAgentRegistry(list);
      setAgentsState(list);
    } catch {}
  }, []);

  const loadRooms = useCallback(async () => {
    try {
      const data = await api.rooms();
      setRooms(data.rooms || []);
      if (!(data.rooms || []).find(r => r.id === currentRoomRef.current)) {
        const def = (data.rooms && data.rooms[0] && data.rooms[0].id) || data.default || 'main';
        setCurrentRoom(def);
        localStorage.setItem('baseagent_room', def);
        navigate(`/chat/${encodeURIComponent(def)}`, { replace: true });
      }
    } catch {}
  }, [navigate]);

  // Контексты нужны экрану комнат, чтобы показать и сменить контекст по умолчанию.
  const loadContexts = useCallback(async () => {
    try { setContexts((await api.contexts()).contexts || []); } catch {}
  }, []);

  const loadMyRequests = useCallback(async () => {
    try {
      const { requests } = await api.humanRequests();
      setMyRequests(requests || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (!me) return;
    loadAgents();
    loadRooms();
    loadContexts();
    loadMyRequests();
  }, [me, loadAgents, loadRooms, loadContexts, loadMyRequests]);

  // ===== room data =====
  const loadRoomData = useCallback(async (roomId) => {
    setLoadingMessages(true);
    try {
      const [msgs, room, tasksRes, apprRes, runsRes] = await Promise.all([
        api.roomMessages(roomId, { limit: HISTORY_PAGE }),
        api.room(roomId),
        api.roomTasks(roomId).catch(() => ({ tasks: [] })),
        api.roomApprovals(roomId).catch(() => ({ approvals: [] })),
        api.roomActiveRuns(roomId).catch(() => ({ runs: [] })),
      ]);
      if (roomId !== currentRoomRef.current) return; // stale response
      setMessages(msgs.messages || []);
      setHasMore((msgs.messages || []).length === HISTORY_PAGE);
      setMembers(room.room.members || []);
      setTasks(tasksRes.tasks || []);
      setApprovals((apprRes.approvals || []).filter(a => a.state === 'pending'));
      // Merge the durable snapshot of live runs with anything already streamed:
      // whichever side has seen more of the stream (higher lastSeq) wins, so a
      // client joining at any moment shows the same state as a live watcher.
      setRuns(prev => {
        const next = new Map(prev);
        for (const s of runsRes.runs || []) {
          const existing = next.get(s.runId);
          if (!existing || (s.lastSeq || 0) >= (existing.lastSeq || 0)) {
            next.set(s.runId, { ...s, progress: existing ? existing.progress : null });
          }
        }
        return next;
      });
    } catch (e) {
      setMessages([]); setMembers([]); setTasks([]); setApprovals([]);
      setHasMore(false);
    }
    setLoadingMessages(false);
  }, []);

  useEffect(() => {
    if (!me) return;
    setRuns(new Map());
    setRunKeys(new Map());
    setReplyTo(null);
    loadRoomData(currentRoom);
    socket.emit('room:join', { roomId: currentRoom }, () => {});
  }, [me, currentRoom, loadRoomData]);

  const loadOlder = useCallback(async () => {
    if (loadingMessages || !hasMore || !messages.length) return;
    setLoadingMessages(true);
    try {
      const data = await api.roomMessages(currentRoom, { limit: HISTORY_PAGE, before: messages[0].ts });
      const older = data.messages || [];
      setHasMore(older.length === HISTORY_PAGE);
      setMessages(prev => [...older, ...prev]);
    } catch {}
    setLoadingMessages(false);
  }, [loadingMessages, hasMore, messages, currentRoom]);

  // ===== socket =====
  useEffect(() => {
    if (!me) return;
    socket.connect();
    return () => socket.disconnect();
  }, [me]);

  // After (re)connect: re-join the viewed room and re-sync its state — events
  // missed while offline are recovered from the durable snapshot.
  const loadRoomDataRef = useRef(null);
  useEffect(() => { loadRoomDataRef.current = loadRoomData; }, [loadRoomData]);
  useEffect(() => {
    const onConnect = () => {
      socket.emit('room:join', { roomId: currentRoomRef.current }, () => {});
      if (loadRoomDataRef.current && !document.hidden) loadRoomDataRef.current(currentRoomRef.current);
    };
    socket.on('connect', onConnect);
    return () => socket.off('connect', onConnect);
  }, []);

  useEffect(() => {
    const onEvt = (evt) => {
      if (!evt || !evt.eventId) return;
      const seen = seenEventsRef.current;
      if (seen.has(evt.eventId)) return;
      seen.add(evt.eventId);
      if (seen.size > 8000) {
        seenEventsRef.current = new Set([...seen].slice(4000));
      }

      // Cross-room signals for the personal inbox badge
      if (evt.type === 'human_request.created' && evt.payload.request && evt.payload.request.participant_id === meRef.current) {
        setMyRequests(prev => prev.some(r => r.id === evt.payload.request.id) ? prev : [...prev, evt.payload.request]);
      }
      if (evt.type === 'human_request.answered') {
        setMyRequests(prev => prev.filter(r => r.id !== evt.payload.requestId));
      }

      if (evt.roomId !== currentRoomRef.current) return;

      switch (evt.type) {
        case 'message.created': {
          const m = evt.payload.message;
          const tempId = evt.payload.clientTempId;
          // A reply produced by a live run TAKES OVER that run's row: the row
          // keeps the key `run-<id>` and the same component, so the working
          // indicator morphs into the message instead of one node being
          // destroyed and another built (Chat contract, pending/morph).
          const taskId = evt.taskId || m.task_id;
          if (taskId && m.sender_type !== 'user') {
            const run = [...runsRef.current.values()].find(r => r.taskId === taskId);
            if (run && m.message_id) {
              setRunKeys(prev => new Map(prev).set(m.message_id, run.runId));
              setRuns(prev => {
                const next = new Map(prev);
                next.delete(run.runId);
                return next;
              });
            }
          }
          setMessages(prev => {
            if (prev.some(x => x.message_id === m.message_id)) return prev;
            // Our own optimistic message: replace it instead of appending a duplicate
            if (tempId && prev.some(x => x.clientTempId === tempId)) {
              return prev.map(x => x.clientTempId === tempId ? { ...m, clientTempId: tempId } : x);
            }
            return [...prev, m];
          });
          break;
        }
        case 'run.created': {
          setRuns(prev => {
            const next = new Map(prev);
            next.set(evt.runId, { runId: evt.runId, taskId: evt.taskId, agentId: evt.payload.agentId, text: '', tools: [], done: false, pid: null, progress: null, lastSeq: 0, startedAt: evt.ts || Date.now() });
            return next;
          });
          break;
        }
        case 'run.started': case 'run.text': case 'run.tool': case 'run.progress': case 'run.result': case 'run.error': {
          setRuns(prev => {
            const next = new Map(prev);
            let run = next.get(evt.runId);
            if (!run) {
              run = { runId: evt.runId, taskId: evt.taskId, agentId: evt.participantId || 'agent', text: '', tools: [], done: false, pid: null, progress: null, lastSeq: 0, startedAt: evt.ts || Date.now() };
            }
            if (evt.seq && evt.seq <= run.lastSeq) return prev; // ordered by (runId, seq)
            run = { ...run, lastSeq: evt.seq || run.lastSeq };
            if (evt.type === 'run.started') run.pid = evt.payload.pid;
            if (evt.type === 'run.text') run.text += evt.payload.text || '';
            if (evt.type === 'run.tool') run.tools = [...run.tools, { name: evt.payload.name, summary: evt.payload.summary }];
            if (evt.type === 'run.result' || evt.type === 'run.error') run.done = true;
            next.set(evt.runId, run);
            return next;
          });
          break;
        }
        case 'run.done': {
          setRuns(prev => {
            const next = new Map(prev);
            const run = next.get(evt.runId);
            if (run) next.set(evt.runId, { ...run, done: true });
            return next;
          });
          // remove the card shortly after; the durable message arrives via message.created
          setTimeout(() => {
            setRuns(prev => {
              const next = new Map(prev);
              next.delete(evt.runId);
              return next;
            });
          }, 600);
          if (evt.payload.state && evt.payload.state !== 'completed' && evt.payload.error) {
            showToast(`Run ${evt.payload.state}: ${evt.payload.error}`);
          }
          break;
        }
        case 'task.progress': {
          setRuns(prev => {
            const next = new Map(prev);
            const run = next.get(evt.payload.runId);
            if (run) next.set(evt.payload.runId, { ...run, progress: { text: evt.payload.text, percent: evt.payload.percent } });
            return next;
          });
          break;
        }
        case 'task.created': {
          setTasks(prev => [evt.payload.task, ...prev.filter(t => t.id !== evt.payload.task.id)]);
          break;
        }
        case 'task.state': {
          setTasks(prev => prev.map(t => t.id === evt.payload.taskId ? { ...t, state: evt.payload.state } : t));
          break;
        }
        case 'approval.created': {
          setApprovals(prev => prev.some(a => a.id === evt.payload.approval.id) ? prev : [...prev, evt.payload.approval]);
          break;
        }
        case 'approval.decided': {
          setApprovals(prev => prev.filter(a => a.id !== evt.payload.approvalId));
          break;
        }
        case 'room.members': {
          setMembers(evt.payload.members || []);
          break;
        }
        default: break;
      }
    };

    socket.on('evt', onEvt);
    return () => socket.off('evt', onEvt);
  }, [me, showToast]);

  // Auto-reload when the server rebuilds the bundle (dev / HMR=1 setups)
  useEffect(() => {
    const onHmrChange = (data) => showToast(`${data && data.file ? data.file : 'обновление'}…`, 'sync');
    const onHmrReload = () => window.location.reload();
    socket.on('hmr:change', onHmrChange);
    socket.on('hmr:reload', onHmrReload);
    return () => {
      socket.off('hmr:change', onHmrChange);
      socket.off('hmr:reload', onHmrReload);
    };
  }, [showToast]);

  // ===== actions =====
  const sendMessage = useCallback(({ text, mentions, attachments, replyToMessageId }) => {
    const clientTempId = 'tmp-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const optimistic = {
      clientTempId, sender: meRef.current, sender_type: 'user', type: 'user',
      text, ts: Date.now(), pending: true, attachments: [],
      // kept for a retry of a failed send — the row stays in the transcript
      draft: { text, mentions, attachments, replyToMessageId },
    };
    setMessages(prev => [...prev, optimistic]);
    socket.emit('chat:send', {
      roomId: currentRoomRef.current, text, mentions, attachments, replyToMessageId, clientTempId,
    }, (ack) => {
      if (ack && ack.ok) {
        setMessages(prev => prev.map(m => m.clientTempId === clientTempId
          ? { ...m, message_id: ack.messageId, ts: ack.ts, pending: false }
          : m));
      } else {
        // The message stays in the transcript as status="failed" — deleting it
        // and saying so in a toast loses the text the person just wrote.
        setMessages(prev => prev.map(m => m.clientTempId === clientTempId
          ? { ...m, pending: false, failed: true }
          : m));
        showToast(`Не отправлено: ${(ack && ack.error) || 'ошибка'}`);
      }
    });
  }, [showToast]);

  // Внутренний сброс (его зовёт и «отправить ещё раз») — без вопросов.
  const removeLocalMessage = useCallback((msg) => {
    setMessages(prev => prev.filter(m => m.clientTempId !== msg.clientTempId));
  }, []);

  // А вот явное «Удалить» в меню сообщения — с вопросом: текст неотправленного
  // сообщения нигде больше не хранится.
  const dropMessage = useCallback(async (msg) => {
    const ok = await confirm({
      title: 'Удалить неотправленное сообщение?',
      description: 'Текст нигде не сохранён — он будет потерян.',
      confirmLabel: 'Удалить',
    });
    if (ok) removeLocalMessage(msg);
  }, [confirm, removeLocalMessage]);

  const retrySend = useCallback((msg) => {
    removeLocalMessage(msg);
    const d = msg.draft || { text: msg.text, mentions: [], attachments: [], replyToMessageId: null };
    sendMessage(d);
  }, [removeLocalMessage, sendMessage]);

  const switchRoom = useCallback((id) => {
    setCurrentRoom(id);
    localStorage.setItem('baseagent_room', id);
    navigate(`/chat/${encodeURIComponent(id)}`);
  }, [navigate]);

  const handleCancelRun = useCallback((run) => {
    socket.emit('run:cancel', { runId: run.runId }, (res) => {
      if (res && res.error) showToast(res.error);
    });
  }, [showToast]);

  const handleDecideApproval = useCallback(async (approvalId, decision) => {
    try {
      await api.decideApproval(approvalId, decision);
      setApprovals(prev => prev.filter(a => a.id !== approvalId));
    } catch (e) { showToast(e.message); }
  }, [showToast]);

  const handleAnswerRequest = useCallback(async (requestId, answer) => {
    try {
      await api.answerHumanRequest(requestId, answer);
      setMyRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (e) { showToast(e.message); }
  }, [showToast]);

  const handleCancelTask = useCallback(async (taskId) => {
    try { await api.cancelTask(taskId); } catch (e) { showToast(e.message); }
  }, [showToast]);

  const handleRetryTask = useCallback(async (taskId) => {
    try { await api.retryTask(taskId); } catch (e) { showToast(e.message); }
  }, [showToast]);

  const handleDeleteRoom = useCallback(async (id) => {
    if (id === 'main') return;
    const room = rooms.find(r => r.id === id);
    const ok = await confirm({
      title: `Удалить комнату «${(room && room.name) || id}»?`,
      description: 'Комната и её состав будут удалены, переписка станет недоступной. Отменить нельзя.',
      confirmLabel: 'Удалить комнату',
    });
    if (!ok) return;
    try {
      await api.deleteRoom(id);
      if (currentRoomRef.current === id) switchRoom('main');
      loadRooms();
    } catch (e) { showToast(e.message); }
  }, [rooms, confirm, switchRoom, loadRooms, showToast]);

  const setRoomContext = useCallback(async (roomId, contextId) => {
    try {
      await api.setRoomDefaultContext(roomId, contextId || null);
      loadRooms();
    } catch (e) { showToast(e.message); }
  }, [loadRooms, showToast]);

  const handleLogout = useCallback(() => {
    // Снапшот админки живёт в модуле и пережил бы смену пользователя во вкладке.
    clearAdminCache();
    api.logout().finally(() => onLoggedOut());
  }, [onLoggedOut]);

  const goToRequest = useCallback(() => {
    if (myRequests.length) {
      const r = myRequests[0];
      switchRoom(r.room_id || currentRoomRef.current);
    }
  }, [myRequests, switchRoom]);

  const isAdmin = myRole === 'admin';
  const roomObj = rooms.find(r => r.id === currentRoom) || { id: currentRoom, name: currentRoom };
  const roomRequests = myRequests.filter(r => r.room_id === currentRoom);

  const destinations = [
    { value: 'chat', icon: 'chat', label: 'Чат', badge: myRequests.length || undefined },
    { value: 'rooms', icon: 'meeting_room', label: 'Комнаты' },
    { value: 'tasks', icon: 'account_tree', label: 'Задачи' },
    ...(isAdmin ? [{ value: 'admin', icon: 'admin_panel_settings', label: 'Админка' }] : []),
  ];

  const headline = navValue === 'chat' ? (roomObj.name || currentRoom) : NAV_TITLES[navValue] || 'BaseAgent';

  return (
    <>
      <AppShell
        appBar={(
          <AppBar size="small">
            <AppBar.Headline>{headline}</AppBar.Headline>
            <AppBar.Trailing>
              {myRequests.length > 0 && navValue !== 'chat' && (
                <IconButton icon="mark_unread_chat_alt" tone="error" label={`Вопросы вам: ${myRequests.length}`} onClick={goToRequest} />
              )}
              {navValue === 'chat' && (
                <IconButton icon="group" label="Участники" onClick={() => setMembersRoom(roomObj)} />
              )}
              <IconButton icon="logout" label="Выйти" onClick={handleLogout} />
            </AppBar.Trailing>
          </AppBar>
        )}
        rail={(
          // Рельс только в свёрнутом виде: expanded={false} — контролируемое
          // состояние без обработчика, развернуться он не может, поэтому
          // NavigationRail.Menu (тот самый переключатель) убран как ненужный.
          <NavigationRail aria-label="Primary" align="center" expanded={false} value={navValue} onValueChange={goTab}>
            {destinations.map(d => (
              <NavigationRail.Item key={d.value} value={d.value} icon={d.icon} label={d.label} badge={d.badge} />
            ))}
          </NavigationRail>
        )}
        bar={(
          <NavigationBar aria-label="Primary" value={navValue} onValueChange={goTab}>
            {destinations.map(d => (
              <NavigationBar.Item key={d.value} value={d.value} icon={d.icon} label={d.label} badge={d.badge} />
            ))}
          </NavigationBar>
        )}
      >
        {navValue === 'chat' && (
          <Page>
            {/* Кэп ширины снят везде — плита остаётся ради поля от края и скролла. */}
            <Container maxWidth="none">
              {/* ChatView объявлен через block('fill') — плита читает ось блока
                  с нашего композита (рецепт «Chat screen», closing note). */}
              <ChatView
                  messages={messages}
                  runs={runs}
                  runKeys={runKeys}
                  approvals={approvals}
                  humanRequests={roomRequests}
                  hasMore={hasMore}
                  loading={loadingMessages}
                  members={members}
                  currentRoom={currentRoom}
                  me={me}
                  replyTo={replyTo}
                  onReply={setReplyTo}
                  onCancelReply={() => setReplyTo(null)}
                  onLoadOlder={loadOlder}
                  onSendMessage={sendMessage}
                  onCancelRun={handleCancelRun}
                  onRetrySend={retrySend}
                  onDropMessage={dropMessage}
                  onDecideApproval={handleDecideApproval}
                  onAnswerRequest={handleAnswerRequest}
              />
            </Container>
          </Page>
        )}

        {(navValue === 'rooms' || navValue === 'tasks') && (
        // Экран НЕ красится (implicit) — красит БЛОК внутри плиты, ровно как в
        // чате это делает Chat.Root. Замерено: контент-регион 33,31,36 → блок
        // 20,18,24 → строки-плиты 33,31,36. Инсет блока — домовой (4dp), как у чата.
        <Page>
          {navValue === 'rooms' && (
            // Плита обязательна: экран сам не скроллится и не даёт полей —
            // ширину, отступ от края окна и прокрутку владеет Container.
            // `--iq-margin: 0` — санкционированная ручка плиты: её внутренняя
            // обёртка (.iq-inner) в flow-режиме кладёт линейку 24dp на ОБЕ оси,
            // и блок отходит от светлого региона на 24px сверху и по бокам.
            // В чате этого нет: там плита в fill-режиме и линейка обнуляется
            // самой библиотекой. С нулём блок садится на домовые 4dp.
            <Container maxWidth="none" style={{ '--iq-margin': 0 }}>
              <Box surface="ground" blockRoot>
                <Stack gap={50}>
                  <List aria-label="Комнаты">
                    {rooms.map(r => {
                      const canManage = isAdmin || r.created_by === me;
                      return (
                        <List.Item
                          key={r.id}
                          leading={<Icon name="tag" />}
                          selected={r.id === currentRoom}
                          onClick={() => switchRoom(r.id)}
                          supporting={roomSummary(r, contexts)}
                          // Одна цель на строку вместо кнопки на каждое действие:
                          // список действий комнаты растёт, а меню принимает
                          // третье и четвёртое, не расширяя строку.
                          trailingAction={(
                            <Menu.Root>
                              <Menu.Trigger render={<IconButton icon="more_vert" size="xs" label={`Действия: ${r.name || r.id}`} />} />
                              <Menu.Portal>
                                <Menu.Positioner align="end">
                                  <Menu.Popup>
                                    <Menu.Item onClick={() => setMembersRoom(r)}>
                                      <Menu.ItemIcon name="group" />Участники
                                    </Menu.Item>
                                    <Menu.Item onClick={() => setFilesRoom(r)}>
                                      <Menu.ItemIcon name="folder_open" />Файлы
                                    </Menu.Item>
                                    <Menu.Item onClick={() => setMemoryRoom(r)}>
                                      <Menu.ItemIcon name="neurology" />Память агентов
                                    </Menu.Item>
                                    <Menu.SubmenuRoot>
                                      <Menu.SubmenuTrigger>
                                        <Menu.ItemIcon name="folder" />Контекст по умолчанию
                                      </Menu.SubmenuTrigger>
                                      <Menu.Portal>
                                        <Menu.Positioner>
                                          <Menu.Popup>
                                            <Menu.RadioGroup
                                              value={r.default_context_id || ''}
                                              onValueChange={(v) => setRoomContext(r.id, v)}
                                            >
                                              <Menu.RadioItem value="">Без контекста</Menu.RadioItem>
                                              {contexts.map(c => (
                                                <Menu.RadioItem key={c.id} value={c.id}>{c.name}</Menu.RadioItem>
                                              ))}
                                            </Menu.RadioGroup>
                                          </Menu.Popup>
                                        </Menu.Positioner>
                                      </Menu.Portal>
                                    </Menu.SubmenuRoot>
                                    {canManage && r.id !== 'main' && (
                                      <>
                                        <Menu.Separator />
                                        <Menu.Item onClick={() => handleDeleteRoom(r.id)}>
                                          <Menu.ItemIcon name="delete" />Удалить комнату
                                        </Menu.Item>
                                      </>
                                    )}
                                  </Menu.Popup>
                                </Menu.Positioner>
                              </Menu.Portal>
                            </Menu.Root>
                          )}
                        >
                          {r.name || r.id}
                        </List.Item>
                      );
                    })}
                  </List>
                  <Cluster justify="center">
                    <Button variant="tonal" onClick={() => setShowCreateRoom(true)}>Создать комнату</Button>
                  </Cluster>
                </Stack>
              </Box>
            </Container>
          )}

          {navValue === 'tasks' && (
            <Container maxWidth="none" style={{ '--iq-margin': 0 }}>
              <Box surface="ground" blockRoot>
                <TasksPanel tasks={tasks} onCancel={handleCancelTask} onRetry={handleRetryTask} />
              </Box>
            </Container>
          )}

        </Page>
        )}

        {navValue === 'admin' && isAdmin && (
        // Админку красят её СЕКЦИИ — у каждой свой `Box surface="ground" blockRoot`
        // (рецепт «Settings screen»), — поэтому экран остаётся implicit и не тратит
        // ступень тона, на которой стоят эти блоки. Ни обёртки-ground вокруг всей
        // страницы, ни padding поверх неё: отступы приходят из дома.
        <Page>
          {/* Плита обязательна: она владеет полем от края окна и скроллом экрана
              (без неё Page ругается «All content must be in a pane», а длинная
              страница остаётся непрокручиваемой). `maxWidth="none"` снимает
              ТОЛЬКО кэп ширины — поле и прокрутка остаются на плите. */}
          <Container maxWidth="none" style={{ '--iq-margin': 0 }}>
            <AdminPanel onAgentsChanged={loadAgents} onError={showToast} />
          </Container>
        </Page>
        )}
      </AppShell>

      {showCreateRoom && (
        <CreateRoomDialog
          open={showCreateRoom}
          onClose={() => setShowCreateRoom(false)}
          agents={agents}
          contexts={contexts}
          me={me}
          onCreate={async (data) => {
            await api.createRoom(data);
            setShowCreateRoom(false);
            loadRooms();
          }}
        />
      )}

      {membersRoom && (
        <RoomMembersDialog
          open
          onClose={() => setMembersRoom(null)}
          room={membersRoom}
          // Для открытой комнаты состав живой (его правит сокет), для остальных —
          // тот, что приехал со списком комнат.
          members={membersRoom.id === currentRoom ? members : (membersRoom.members || [])}
          agents={agents}
          canManage={isAdmin || membersRoom.created_by === me}
          onAddMember={async (p) => {
            try { await api.addRoomMember(membersRoom.id, p); loadRooms(); } catch (e) { showToast(e.message); }
          }}
          onRemoveMember={async (p) => {
            const ok = await confirm({
              title: `Убрать @${p} из комнаты «${membersRoom.name || membersRoom.id}»?`,
              description: 'Участник потеряет доступ к комнате и её переписке. Вернуть можно, добавив заново.',
              confirmLabel: 'Убрать',
              icon: 'person_remove',
            });
            if (!ok) return;
            try { await api.removeRoomMember(membersRoom.id, p); loadRooms(); } catch (e) { showToast(e.message); }
          }}
        />
      )}

      {confirmDialog}

      {memoryRoom && <RoomMemoryDialog room={memoryRoom} onClose={() => setMemoryRoom(null)} />}
      {filesRoom && <RoomFilesDialog room={filesRoom} onClose={() => setFilesRoom(null)} />}

      {toast && (
        <Box surface="inverse-surface" radius="full" paddingX={200} paddingY={100}>
          <Cluster gap={100} align="center" wrap={false}>
            {toast.icon && <Icon name={toast.icon} size="s" />}
            <Text variant="body-small" render={<span />}>{toast.text}</Text>
          </Cluster>
        </Box>
      )}
    </>
  );
}
